<script lang="ts">
	import type { USPBlock } from '$models/Block/USPBlock';
	import Button from '$components/UI/Buttons/Button.svelte';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { sanitizeHtml } from '$lib/utils';

	interface Props {
		block: USPBlock;
	}
	const { block }: Props = $props();
</script>

<div class="usps-container">
	<div class="usps">
		<div class="usps-layout usps-layout--count-{block.usps.length}">
			{#if block.title || block.text || block.button}
				<div class="usps-header">
					{#if block.title}
						<h2 class="usps-header-title">{@html block.title}</h2>
					{/if}
					{#if block.text}
						<div class="usps-header-text rich-text">
							{@html sanitizeHtml(block.text)}
						</div>
					{/if}
					{#if block.button}
						<div class="usps-header-button">
							<Button type="anchor" href={block.button.url} variant="dark">
								{block.button.title}
							</Button>
						</div>
					{/if}
				</div>
			{/if}

			{#if block.usps.length > 0}
				<div class="usps-grid">
					{#each block.usps as item}
						<div class="usps-item">
							{#if item.image}
								<figure class="usps-item-image">
									<img
										src={item.image.url}
										srcset={getWordPressImageSrcSet(item.image)}
										sizes="(min-width: 1000px) 25cqw, 100cqw"
										alt={item.image.alt}
										width={item.image.width}
										height={item.image.height}
										loading="lazy"
										decoding="async"
									/>
								</figure>
							{/if}
							<div class="usps-item-content">
								<h3 class="usps-item-title">{@html item.title}</h3>
								<div class="usps-item-text rich-text">
									{@html item.text}
								</div>
							</div>
						</div>
					{/each}
				</div>
			{/if}

			{#if block.button}
				<div class="usps-footer">
					<Button type="anchor" href={block.button.url} variant="dark">
						{block.button.title}
					</Button>
				</div>
			{/if}
		</div>
	</div>
</div>

<style lang="postcss">
	.usps-container {
		container-type: inline-size;
		container-name: usps;
	}

	.usps {
		background-color: var(--variant-tint-3-color);
		padding: 32px 16px;
		border-radius: 16px;
		overflow: hidden;

		&-layout {
			display: flex;
			flex-direction: column;
			gap: 40px;
		}

		&-header {
			&-title {
				font-family: var(--block-header-title-font-family);
				font-weight: var(--block-header-title-font-weight);
				font-size: 28px;
				line-height: 1;
				text-transform: var(--block-header-title-transform);

				&:not(:last-child) {
					margin-bottom: 4px;
				}
			}

			&-text {
				font-size: 16px;
				line-height: 1.6;
			}

			&-button {
				display: none;
			}
		}

		&-grid {
			display: grid;
			grid-template-columns: 1fr;
		}

		&-item {
			position: relative;
			display: flex;
			align-items: center;
			gap: 16px;
			padding: 16px 0;
			min-height: 132px;

			&::after {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}

			&:first-child::before {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				top: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}

			&-image {
				display: flex;
				align-items: center;
				aspect-ratio: 1;
				max-width: 80px;
				flex-shrink: 0;

				img {
					display: block;
				}
			}

			&-content {
				display: flex;
				flex-direction: column;
			}
		}

		&-footer {
			display: flex;
			justify-content: center;
		}

		@media (--md) {
			&-header {
				&-title {
					font-size: 40px;
				}

				&-text {
					font-size: 18px;
				}
			}

			&-item-text {
				font-size: 18px;
			}
		}

		@media (--lg) {
			&-item {
				&-title {
					font-size: 20px;
				}

				&-text {
					font-size: 16px;
				}
			}
		}
	}

	@container usps (min-width: 480px) {
		.usps {
			padding: 40px;

			&-header {
				margin-bottom: 16px;
			}

			&-item {
				gap: 32px;

				&-image {
					max-width: 100px;
				}
			}
		}
	}

	@container usps (min-width: 1000px) {
		.usps {
			&-layout {
				flex-direction: row;
				gap: 40px;
			}

			&-layout--count-2 .usps-grid {
				grid-template-columns: repeat(2, minmax(0, 50%));
			}

			&-layout--count-3 .usps-grid {
				grid-template-columns: repeat(3, minmax(0, 33.33%));
			}

			&-header {
				flex-grow: 1;

				&-title {
					&:not(:last-child) {
						margin-bottom: 16px;
					}
				}

				&-text {
					&:not(:last-child) {
						margin-bottom: 32px;
					}
				}

				&-button {
					display: block;
				}

				&:not(:last-child) {
					margin-bottom: 0;
				}
			}

			&-grid {
				width: 70%;
				flex-shrink: 0;
				gap: 0;
			}

			&-footer {
				display: none;
			}

			&-item {
				flex-direction: column;
				align-items: flex-start;
				justify-content: center;
				padding: 16px 32px;
				gap: 16px;
				min-height: 0;

				&::after {
					display: none;
				}

				&:first-child::before,
				&::before {
					content: '';
					position: absolute;
					top: 0;
					bottom: 0;
					left: 0;
					right: auto;
					width: 2px;
					height: auto;
					background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='2' height='5' viewBox='0 0 2 5'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
					background-size: 2px 5px;
					background-repeat: repeat-y;
					pointer-events: none;
				}

				&-image {
					max-width: 120px;
				}

				&-title {
					&:not(:last-child) {
						margin-bottom: 4px;
					}
				}
			}
		}
	}

	@container usps (min-width: 1200px) {
		.usps {
			&-grid {
				width: 75%;
			}

			&-item {
				padding: 16px 40px;
			}
		}
	}
</style>
